import { MarketCode } from '@tink/config/src/markets';
import { ProductType } from '@tink/core/src/types';
import { DeviceType } from '@tink/tink-link-core/src/user-agent-detector';
import { useUrlQuery } from '../components/ApplicationProviders/UrlQueryProvider/UrlQueryProvider';
import { useAnalytics } from '../core/Analytics/AnalyticsProvider';
import { PageView } from '../models/analytics';
import {
  isUpdateConsent,
  ProductDimension,
  ProductDimensionCredentials,
  ProductDimensionPay,
} from '../models/product';
import { Provider, ProviderEntity } from '../models/provider';
import { isInTestGroup } from './ab-testing-group-calculator';
import { GradualReleaseLabels, TestExposure } from './useGradualRolloutTypes';

type Delimiters = {
  byMarket: (market?: MarketCode) => TestExposure;
  byProvider: (provider?: ProviderEntity) => TestExposure;
  byProviderName: (providerName?: string) => TestExposure;
  byDevice: (device?: DeviceType) => TestExposure;
  byProduct: (productType?: ProductType, productDimension?: ProductDimension) => TestExposure;
  byClientId: (clientId?: string) => TestExposure;
};

type GradualReleaseOptions = {
  testGroupLimit: number;
  partialDelimiters?: Partial<Delimiters>;
};

type GradualReleaseGroup = Record<GradualReleaseLabels, GradualReleaseOptions>;

export const marketInTest = (allowedMarkets: MarketCode[]) => (market?: MarketCode) => {
  const includedMarket = allowedMarkets.some((m) => market === m);

  if (includedMarket) {
    return TestExposure.IN_TEST;
  }

  return TestExposure.OUTSIDE_TEST;
};

export const productInTest =
  (allowedProduct: ProductType, allowedDimensions: ProductDimension[]) =>
  (productType?: ProductType, productDimension?: ProductDimension) => {
    if (
      productType &&
      productDimension &&
      productType === allowedProduct &&
      allowedDimensions.some((dimension) => dimension === productDimension)
    ) {
      return TestExposure.IN_TEST;
    }

    return TestExposure.OUTSIDE_TEST;
  };

export const marketOutsideTest = (excludedMarkets: MarketCode[]) => (market?: MarketCode) => {
  const excludedMarket = excludedMarkets.some((m) => market === m);

  if (excludedMarket) {
    return TestExposure.OUTSIDE_TEST;
  }

  return TestExposure.IN_TEST;
};

export const gradualReleaseGroups: GradualReleaseGroup = {
  [GradualReleaseLabels.MOCKED_RELEASE_LABEL]: { testGroupLimit: 0 },
  [GradualReleaseLabels.LOGIN_MACHINE_REFRESH]: {
    testGroupLimit: 0,
    partialDelimiters: {
      byProduct: (productType, productDimension) => {
        if (
          productType &&
          productDimension &&
          productType === ProductType.CREDENTIALS &&
          productDimension === ProductDimensionCredentials.REFRESH
        ) {
          return TestExposure.IN_TEST;
        }

        return TestExposure.OUTSIDE_TEST;
      },
    },
  },
  [GradualReleaseLabels.LOGIN_MACHINE_UPDATE_CONSENT]: {
    testGroupLimit: 0,
    partialDelimiters: {
      byProduct: (productType, productDimension) => {
        if (productType && productDimension && isUpdateConsent(productType, productDimension)) {
          return TestExposure.IN_TEST;
        }

        return TestExposure.OUTSIDE_TEST;
      },
    },
  },
  [GradualReleaseLabels.THIRD_PARTY_REDIRECT_MACHINE]: {
    testGroupLimit: 0,
  },
  [GradualReleaseLabels.TEMPLATE]: {
    testGroupLimit: 0.5,
    partialDelimiters: {
      byProvider: (provider) => {
        if (provider && Provider.hasLoginHeaderColour(provider)) {
          return TestExposure.IN_TEST;
        }

        return TestExposure.OUTSIDE_TEST;
      },
    },
  },
  [GradualReleaseLabels.BAC_PROVIDERS]: {
    testGroupLimit: 0,
  },
  [GradualReleaseLabels.BTRX_PROVIDERS]: {
    testGroupLimit: 0,
  },
  [GradualReleaseLabels.CREDENTIALS_LOADING_SCREEN]: {
    testGroupLimit: 1,
    partialDelimiters: {
      byProduct: (product) => {
        const inTransactions = product === ProductType.TRANSACTIONS;
        const inIncomeCheck = product === ProductType.INCOME_CHECK;

        return inTransactions || inIncomeCheck ? TestExposure.IN_TEST : TestExposure.OUTSIDE_TEST;
      },
    },
  },
  [GradualReleaseLabels.PAYMENT_CONSENT_SCREEN_DE_50]: {
    testGroupLimit: 0.5,
    partialDelimiters: {
      byProduct: productInTest(ProductType.PAY, [ProductDimensionPay.DIRECT]),
      byMarket: marketInTest([MarketCode.DE]),
      byClientId: (clientId) => {
        const clientList = [
          'e03b9a75846746f5851af92442caba82', // Bitpanda
          'b54ae4a0d4bd42fbbef5247b711a457a', // PayOp
        ];

        if (clientList.includes(clientId || '')) return TestExposure.IN_TEST;

        return TestExposure.OUTSIDE_TEST;
      },
    },
  },
  [GradualReleaseLabels.PAYMENT_CONSENT_SCREEN_100]: {
    testGroupLimit: 1,
    partialDelimiters: {
      byProduct: productInTest(ProductType.PAY, [ProductDimensionPay.DIRECT]),
      byProviderName: (providerName) => {
        if (providerName && Provider.isRequiringPaymentConsentScreen(providerName)) {
          return TestExposure.IN_TEST;
        }

        return TestExposure.OUTSIDE_TEST;
      },
    },
  },
  [GradualReleaseLabels.ACCOUNT_SELECTION_SCREEN_AC_50]: {
    testGroupLimit: 0.5,
    partialDelimiters: {
      byProduct: (product) => {
        const inAccountCheck = product === ProductType.ACCOUNT_CHECK;

        return inAccountCheck ? TestExposure.IN_TEST : TestExposure.OUTSIDE_TEST;
      },
    },
  },
  [GradualReleaseLabels.XSTATE_CREDENTIALS_MACHINE]: {
    testGroupLimit: 0,
  },
  [GradualReleaseLabels.DEFAULT_FLOWS_PROVIDER_LIST_50]: {
    testGroupLimit: 0.5,
    partialDelimiters: {
      byMarket: (market) => {
        return marketInTest([
          MarketCode.SE,
          MarketCode.GB,
          MarketCode.ES,
          MarketCode.IT,
          MarketCode.DK,
          MarketCode.NO,
          MarketCode.PT,
          MarketCode.FR,
        ])(market);
      },
      byProduct: (product) => {
        if (product === ProductType.PAY) {
          return TestExposure.OUTSIDE_TEST;
        }

        return TestExposure.IN_TEST;
      },
    },
  },
  [GradualReleaseLabels.DEFAULT_FLOWS_BRANCH_SELECTION_AB_50]: {
    testGroupLimit: 0.5,
    partialDelimiters: {
      byMarket: marketInTest([MarketCode.DE]),
      byProvider: (provider) => {
        if (
          provider &&
          (provider.groupDisplayName === 'Postbank' ||
            provider.groupDisplayName === 'Volks- und Raiffeisenbanken')
        ) {
          return TestExposure.IN_TEST;
        }

        return TestExposure.OUTSIDE_TEST;
      },
      byClientId: (clientId) => {
        const adyenClientId = '7e88503d4e944de691cd9ea33981ef50';

        if (clientId && clientId !== adyenClientId) {
          return TestExposure.IN_TEST;
        }

        return TestExposure.OUTSIDE_TEST;
      },
    },
  },
  [GradualReleaseLabels.DEFAULT_FLOWS_REGION_SELECTION_40]: {
    testGroupLimit: 0.4,
    partialDelimiters: {
      byMarket: marketInTest([MarketCode.DE]),
      byClientId: (clientId) => {
        const adyenClientId = '7e88503d4e944de691cd9ea33981ef50';

        if (clientId && clientId !== adyenClientId) {
          return TestExposure.IN_TEST;
        }

        return TestExposure.OUTSIDE_TEST;
      },
    },
  },
  // This label is used in conjunction with the DEFAULT_FLOWS_REGION_SELECTION_40 above.
  // Since 50% of 40% is 20%, this is then reflected in the name of the label.
  [GradualReleaseLabels.DEFAULT_FLOWS_REGION_SELECTION_AB_20]: {
    testGroupLimit: 0.5,
    partialDelimiters: {
      byMarket: marketInTest([MarketCode.DE]),
      byClientId: (clientId) => {
        const adyenClientId = '7e88503d4e944de691cd9ea33981ef50';

        if (clientId && clientId !== adyenClientId) {
          return TestExposure.IN_TEST;
        }

        return TestExposure.OUTSIDE_TEST;
      },
    },
  },
  [GradualReleaseLabels.XSTATE_CREDENTIALS_MACHINE_FR_REDIRECT_10]: {
    testGroupLimit: 0.1,
    partialDelimiters: {
      byMarket: marketInTest([MarketCode.FR]),
      byProduct: (product) => {
        const isAccountCheck = product === ProductType.ACCOUNT_CHECK;

        return isAccountCheck ? TestExposure.IN_TEST : TestExposure.OUTSIDE_TEST;
      },
      byProvider: (provider) => {
        if (provider && Provider.isRedirectAuthenticationFlow(provider)) {
          return TestExposure.IN_TEST;
        }

        return TestExposure.OUTSIDE_TEST;
      },
    },
  },
  [GradualReleaseLabels.XSTATE_CREDENTIALS_MACHINE_AC_DE_EMBEDDED_10]: {
    testGroupLimit: 0.1,
    partialDelimiters: {
      byMarket: marketInTest([MarketCode.DE]),
      byProduct: (product) => {
        const isAccountCheck = product === ProductType.ACCOUNT_CHECK;

        return isAccountCheck ? TestExposure.IN_TEST : TestExposure.OUTSIDE_TEST;
      },
      byProvider: (provider) => {
        if (provider && Provider.isEmbeddedAuthenticationFlow(provider)) {
          return TestExposure.IN_TEST;
        }

        return TestExposure.OUTSIDE_TEST;
      },
    },
  },
  [GradualReleaseLabels.DEFAULT_FLOWS_LOADING_SPINNER]: {
    testGroupLimit: 0,
  },
};

const defaultDelimiters: Delimiters = {
  byMarket: () => TestExposure.IN_TEST,
  byProvider: () => TestExposure.IN_TEST,
  byProviderName: () => TestExposure.IN_TEST,
  byDevice: () => TestExposure.IN_TEST,
  byProduct: () => TestExposure.IN_TEST,
  byClientId: () => TestExposure.IN_TEST,
};

export type IsInTestGroupPayload = {
  userId: string;
  market?: MarketCode;
  provider?: ProviderEntity;
  providerName?: string;
  device?: DeviceType;
  productType?: ProductType;
  productDimension?: ProductDimension;
  clientId?: string;
};

export type IsInTestGroup = (isInTestGroupPayload: IsInTestGroupPayload) => boolean;

export type UseGradualRollout = (
  view: PageView,
  gradualReleaseLabel: GradualReleaseLabels
) => { isInTestGroup: IsInTestGroup };

export const getTestExposure = (delimitingCriterias: TestExposure[]) => {
  const decisions = new Set(delimitingCriterias);

  if (
    decisions.has(TestExposure.OUTSIDE_TEST_WITH_EXPERIMENTAL_FEATURE) &&
    decisions.has(TestExposure.OUTSIDE_TEST)
  )
    return TestExposure.OUTSIDE_TEST;

  if (decisions.has(TestExposure.OUTSIDE_TEST_WITH_EXPERIMENTAL_FEATURE))
    return TestExposure.OUTSIDE_TEST_WITH_EXPERIMENTAL_FEATURE;

  if (decisions.has(TestExposure.OUTSIDE_TEST)) return TestExposure.OUTSIDE_TEST;

  return TestExposure.IN_TEST;
};

export const useGradualRollout: UseGradualRollout = (view, gradualReleaseLabel) => {
  const { tinklinkAnalytics } = useAnalytics();
  const { releaseGroupToggles } = useUrlQuery();

  const testExposureByUrlQuery = releaseGroupToggles
    ? releaseGroupToggles[gradualReleaseLabel]
    : null;

  const { testGroupLimit, partialDelimiters } = gradualReleaseGroups[gradualReleaseLabel];

  const delimiters = { ...defaultDelimiters, ...partialDelimiters };

  return {
    isInTestGroup: ({
      userId,
      market,
      provider,
      providerName,
      device,
      productType,
      productDimension,
      clientId,
    }) => {
      const testExposure =
        testExposureByUrlQuery ||
        getTestExposure([
          delimiters.byMarket(market),
          delimiters.byProvider(provider),
          delimiters.byProviderName(providerName),
          delimiters.byDevice(device),
          delimiters.byProduct(productType, productDimension),
          delimiters.byClientId(clientId),
        ]);

      switch (testExposure) {
        case TestExposure.OUTSIDE_TEST:
          return false;
        case TestExposure.OUTSIDE_TEST_WITH_EXPERIMENTAL_FEATURE:
          return true;
        default:
          break;
      }

      const abResult = isInTestGroup(testGroupLimit, [userId, gradualReleaseLabel]);

      tinklinkAnalytics.sendInteractionEventOnce({
        view,
        label: abResult.toString(),
        action: ['AB_TEST_SWITCH', gradualReleaseLabel].join('/'),
      });

      return abResult;
    },
  };
};
